import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';
import styled from 'styled-components';
import { updateFirstPageData } from '../../Redux/HealthRedux';
import HealthIcon from '../../assets/health-insurance.png';
import FooterNew from '../../components/Footer/FooterNew';

// Theme Colors
const brandColor = '#F6931E';

// Styled Components
const Container = styled.div`
  display: grid;
  min-height: 80vh;
  background: linear-gradient(to bottom, #f5e3c3, #813b99);
  padding: 40px;
  align-content: center;
`;

const FormWrapper = styled.div`
  background: #fff;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  width: 380px;
  text-align: center;
`;

const Title = styled.h2`
  color: #333;
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 15px;
  font-family: 'Poppins', sans-serif;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: bold;
  color: #444;
  display: block;
  margin-bottom: 6px;
  text-align: left;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 5px;
  border: 1px solid ${({ error }) => (error ? 'red' : '#ddd')};
  border-radius: 8px;
  outline: none;
  font-size: 14px;
  background: #fff;
  transition: 0.3s ease-in-out;

  &:focus {
    border-color: ${({ error }) => (error ? 'red' : brandColor)};
    box-shadow: 0 0 5px
      ${({ error }) =>
        error ? 'rgba(255, 0, 0, 0.5)' : 'rgba(246, 147, 30, 0.5)'};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ error }) => (error ? 'red' : '#ddd')};
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
  margin-bottom: 5px;
  transition: 0.3s ease-in-out;

  &:focus {
    border-color: ${({ error }) => (error ? 'red' : brandColor)};
    box-shadow: 0 0 5px
      ${({ error }) =>
        error ? 'rgba(255, 0, 0, 0.5)' : 'rgba(246, 147, 30, 0.5)'};
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(45deg, #ff6a3a, ${brandColor});
  color: white;
  font-size: 16px;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 20px;
  transition: all 0.3s ease-in-out;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.3);

  &:hover {
    transform: translateY(-3px);
    background: linear-gradient(45deg, #ff6a3a, ${brandColor});
    box-shadow: 0 0 20px rgba(255, 87, 51, 0.5);
  }
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: red;
  text-align: left;
  margin-bottom: 10px;
`;

const ContainerData = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 20px;
  justify-items: center;
  align-items: center;
`;

const HealthInsuranceForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Local State
  const [formData, setFormData] = useState({
    mobile: '',
    email: '',
    pinCode: '',
    tenure: '',
  });

  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [loadingPincode, setLoadingPincode] = useState(false);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    if (name === 'mobile') {
      // Allow only digits, max length 10
      const onlyNums = value.replace(/\D/g, '').slice(0, 10);
      setFormData({
        ...formData,
        mobile: onlyNums,
      });
      return;
    }

    if (name === 'pinCode') {
      // Allow only digits, max length 6
      const onlyNums = value.replace(/\D/g, '').slice(0, 6);
      setFormData({
        ...formData,
        pinCode: onlyNums,
      });

      if (onlyNums.length === 6) {
        setLoadingPincode(true);
        try {
          const res = await fetch(
            `https://api.postalpincode.in/pincode/${onlyNums}`
          );
          const data = await res.json();
          if (data && data[0]?.Status === 'Success') {
            const postOffices = data[0].PostOffice;
            if (postOffices.length > 0) {
              const options = postOffices.map((po) => ({
                pinCode: po.Pincode,
                district: po.District,
                name: po.Name,
              }));
              setPincodeOptions(options);
            } else {
              setPincodeOptions([]);
            }
          } else {
            setPincodeOptions([]);
          }
        } catch (error) {
          console.error('Error fetching pincode:', error);
          setPincodeOptions([]);
        }
        setLoadingPincode(false);
      } else {
        setPincodeOptions([]);
      }
      return;
    }

    const numberFields = ['tenure'];
    const newValue = numberFields.includes(name) ? Number(value) : value;

    setFormData({
      ...formData,
      [name]: newValue,
    });
  };

  const handleContinue = (e) => {
    e.preventDefault();

    let errors = {};

    // Mobile validation
    if (!formData.mobile) {
      errors.mobile = 'Please enter your mobile number.';
    } else if (formData.mobile.length !== 10) {
      errors.mobile = 'Mobile number must be 10 digits.';
    }

    // Email validation
    if (!formData.email) {
      errors.email = 'Please enter your email.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Please enter a valid email address.';
      }
    }

    // Pin code validation
    if (!formData.pinCode) {
      errors.pinCode = 'Please enter your pin code.';
    } else if (formData.pinCode.length !== 6) {
      errors.pinCode = 'Pin code must be 6 digits.';
    }

    // Tenure validation
    if (!formData.tenure) errors.tenure = 'Please select a tenure.';

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Clear errors if all validations pass
    setValidationErrors({});
    setError('');

    const obj = {
      enquiryId: uuid(),
      userDetail: {
        phone: formData.mobile,
        email: formData.email,
        pinCode: formData.pinCode,
        city: '', // You can update this if you want to fetch city based on pincode
      },
      tenure: formData.tenure,
    };

    dispatch(updateFirstPageData(obj))
      .then(() => {
        navigate(`/member-selection?enquiryId=${obj.enquiryId}`);
      })
      .catch((error) => {
        console.error('Error updating data:', error);
        setError('Failed to update data. Please try again later.');
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Container>
        <ContainerData>
          <FormWrapper>
            <Title>Get Health Insurance</Title>
            <form onSubmit={handleContinue}>
              {/* Mobile Number Input */}
              <div>
                <Label>Mobile Number<span style={{ color: 'red' }}>*</span></Label>
                <Input
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder="Enter your mobile number"
                  error={validationErrors.mobile}
                  maxLength={10}
                />
                {validationErrors.mobile && (
                  <ErrorText>{validationErrors.mobile}</ErrorText>
                )}
              </div>

              {/* Email Input */}
              <div>
                <Label>Email<span style={{ color: 'red' }}>*</span></Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  error={validationErrors.email}
                />
                {validationErrors.email && (
                  <ErrorText>{validationErrors.email}</ErrorText>
                )}
              </div>

              {/* Pin Code Input */}
              <div>
                <Label>Pin Code<span style={{ color: 'red' }}>*</span></Label>
                <Input
                  list="pincode-suggestions"
                  type="text"
                  name="pinCode"
                  value={formData.pinCode}
                  onChange={handleInputChange}
                  placeholder="Enter your pin code"
                  error={validationErrors.pinCode}
                  maxLength={6}
                />
                <datalist id="pincode-suggestions">
                  {pincodeOptions.map((option, index) => (
                    <option
                      key={index}
                      value={`${option.pinCode} - ${option.name}, ${option.district}`}
                    />
                  ))}
                </datalist>
                {validationErrors.pinCode && (
                  <ErrorText>{validationErrors.pinCode}</ErrorText>
                )}
              </div>

              {/* Tenure Dropdown */}
              <div>
                <Label>Tenure<span style={{ color: 'red' }}>*</span></Label>
                <Select
                  name="tenure"
                  value={formData.tenure}
                  onChange={handleInputChange}
                  error={validationErrors.tenure}
                >
                  <option value="">Select tenure</option>
                  <option value="1">1 Years</option>
                  <option value="2">2 Years</option>
                  <option value="3">3 Years</option>
                  <option value="4">4 Years</option>
                  <option value="5">5 Years</option>
                </Select>
                {validationErrors.tenure && (
                  <ErrorText>{validationErrors.tenure}</ErrorText>
                )}
              </div>

              {/* Submit Button */}
              <Button type="submit">Get Quotes</Button>
              {error && <ErrorText>{error}</ErrorText>}
            </form>
          </FormWrapper>
          <img
            src={HealthIcon}
            alt="Health Insurance"
            style={{ width: '300px' }}
          />
        </ContainerData>
      </Container>
      <FooterNew />
    </>
  );
};

export default HealthInsuranceForm;
